import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Brain, Clock, Leaf, Sparkles, Info, Activity, ArrowRight } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const StressWellbeingPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-rose-100 rounded-xl">
              <Heart className="h-8 w-8 text-rose-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-rose-600 mb-1">Integrative Health</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Stress & Mental Wellbeing
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Stress & Mental Wellbeing"
            category="Stress"
            sourcePage="Stress & Mental Wellbeing"
            sourceUrl="/stress-wellbeing"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: Understanding stress */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            Understanding stress and its impact on health
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Stress is not just an emotional experience. It is a physical response that affects many systems in the body, including hormones, the immune system, sleep, digestion, and energy levels.
            </p>
            <p>
              Living with a chronic condition like Chronic Lymphocytic Leukaemia (CLL) can create ongoing stress, even when the disease itself is stable.
            </p>
          </div>
        </div>

        {/* Section 2: Stress during Watch and Wait */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-blue-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Stress during Watch and Wait
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Many people with CLL describe stress related to:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Uncertainty about the future</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Waiting for blood results</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Interpreting changes in numbers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Feeling well but knowing they have a diagnosis</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  These feelings are common and understandable.
                </p>
                <div className="mt-4 bg-white border border-blue-200 rounded-xl p-4">
                  <p className="text-slate-700">Stress does not mean you are coping poorly.</p>
                  <p className="font-semibold text-blue-800 mt-1">It means you are human.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: How long-term stress affects the body */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0 mt-1">
              <Brain className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                How long-term stress can affect the body
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">When stress becomes ongoing, it can:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Disrupt sleep</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Increase fatigue</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Affect immune regulation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Impact concentration and mood</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Make physical symptoms feel more intense</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Managing stress is not about eliminating it, but about reducing its impact over time.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Gentle approaches */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-green-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <Leaf className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Gentle approaches to managing stress
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">
                  There is no single "right" way to manage stress. Many people find benefit in simple, repeatable practices such as:
                </p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Walking regularly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Gentle physical movement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Breathing exercises</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Spending time outdoors</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Establishing daily routines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Limiting constant checking of results or information</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  Small, consistent actions often matter more than big changes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Mental wellbeing and self-compassion */}
        <div className="p-6 md:p-8 bg-rose-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-rose-100 rounded-lg flex-shrink-0 mt-1">
              <Sparkles className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Mental wellbeing and self-compassion
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  It is common to feel anxious, frustrated, or low at times. These emotions do not need to be fixed or pushed away.
                </p>
                <p className="font-medium text-slate-800">
                  Noticing how you feel, acknowledging it, and giving yourself permission to rest are important parts of wellbeing.
                </p>
                <div className="mt-4 bg-white border border-rose-200 rounded-xl p-4">
                  <p className="text-slate-700 mb-2">This platform includes tools to help you:</p>
                  <ul className="space-y-2 ml-1">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-rose-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Track how you feel over time</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-rose-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Observe patterns alongside sleep, activity, and blood results</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-rose-500 rounded-full mt-2 flex-shrink-0"></span>
                      <span>Prepare for conversations with your healthcare team</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related: Exercise & Movement */}
      <Link
        to="/exercise-movement"
        className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-md hover:border-slate-300 transition-all"
        data-testid="stress-to-exercise-link"
      >
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-teal-50 rounded-xl">
            <Activity className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">Exercise & Movement</p>
            <p className="text-xs text-slate-500">Gentle activity is one of the better-supported ways to ease stress and fatigue</p>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
      </Link>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              This page is for educational and supportive purposes only. It does not replace professional mental health care. If stress or anxiety feels overwhelming, support from a qualified professional is encouraged.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Stress & Mental Wellbeing"
          category="Stress"
          sourcePage="Stress & Mental Wellbeing"
          sourceUrl="/stress-wellbeing"
        />
      </div>
    </div>
  );
};

export default StressWellbeingPage;
